/**
 * Modal dedicado para **revisar / editar** as notas da proposta gerada
 * pela IA (PR-M1-01c-i.ux-fix).
 *
 * **Motivação** (relato do Alexandre): após o "Gerar rascunho com IA"
 * popular todos os campos, o textarea "Notas da proposta" ficava
 * longe no scroll do dialog e atrapalhava leitura + edição. Este modal:
 *
 *   - Abre automaticamente após sucesso do `onGenerate` no
 *     `NewProposalDialog` (ver handler) — foca 100% na leitura.
 *   - Pode ser reaberto manualmente pelo botão "Expandir" ao lado
 *     do textarea no form principal (caso Karla tenha fechado e
 *     queira revisitar).
 *   - Textarea grande (altura baseada em dvh para mobile confortável).
 *   - Ao clicar "Aplicar", grava o valor no form e fecha.
 *   - Ao cancelar, mantém o valor anterior — não desfaz o que a IA
 *     gerou nos outros campos.
 *
 * **Dialog aninhado** (dentro do `NewProposalDialog` que já é Dialog):
 * Radix portals cuidam da ordem de empilhamento automaticamente.
 * Cada Dialog cria seu próprio overlay + container focus trap.
 *
 * Design System:
 *   - Zero hardcode (só tokens).
 *   - Mobile: ocupa quase tela toda (`max-h-[calc(100dvh-2rem)]`).
 *   - Desktop: `sm:max-w-2xl` — ligeiramente mais largo que o dialog
 *     pai (2xl ambos) para dar respiro à leitura sem quebrar o eixo.
 *   - Tap targets size="lg" (≥44px).
 *   - Textarea `text-base` em mobile (evita zoom no iOS).
 */
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@kmgestao/ui';

export interface NewProposalNotesModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Valor atual das notas (sincronizado com o form pai via `form.getValues('notes')`). */
  value: string;
  /** Disparado quando user clica "Aplicar" — pai faz `setValue('notes', ...)`. */
  onApply: (nextValue: string) => void;
}

export function NewProposalNotesModal({
  open,
  onOpenChange,
  value,
  onApply,
}: NewProposalNotesModalProps) {
  // Buffer local editável — só persiste no form ao clicar "Aplicar".
  // Permite cancelamento sem afetar o estado do dialog pai.
  const [draft, setDraft] = useState(value);

  // Ref para focar o textarea programaticamente ao abrir (em vez de
  // `autoFocus`, que o jsx-a11y bloqueia). Radix Dialog já gerencia
  // focus trap; aqui só realocamos o foco inicial para o textarea.
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Resync quando abrir com valor novo (ex: IA gerou conteúdo fresco).
  useEffect(() => {
    if (open) {
      setDraft(value);
      // Foca após o próximo frame para garantir que o Radix já montou
      // o conteúdo e não vai roubar o foco em seguida.
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [open, value]);

  const handleApply = () => {
    onApply(draft);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Revisar notas da proposta</DialogTitle>
          <DialogDescription>
            Esse é o corpo da proposta que o lead vai receber. Ajuste o tom, corrija detalhes,
            adicione ou remova informações. Ao clicar em Aplicar, as notas voltam para o formulário
            e você finaliza os demais campos antes de criar o rascunho.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-2 py-2">
          <Label htmlFor="notes-modal-textarea" className="sr-only">
            Notas da proposta
          </Label>
          <textarea
            ref={textareaRef}
            id="notes-modal-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={4_000}
            placeholder="Corpo da proposta — tom profissional e cordial, 2 a 4 parágrafos."
            className="flex min-h-[50vh] w-full flex-1 resize-none rounded-md border border-input bg-background p-3 text-base leading-relaxed ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm md:leading-6"
          />
          <p className="text-right text-xs text-muted-foreground">
            {draft.length.toLocaleString('pt-BR')} / 4.000 caracteres
          </p>
        </div>

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg">
              Cancelar
            </Button>
          </DialogClose>
          <Button type="button" size="lg" onClick={handleApply}>
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
